import React, { useState, useEffect } from 'react';
import { getAllSlides, createSlide, updateSlide, deleteSlide, toggleSlideStatus } from '../../services/api';
import DataTable from '../components/DataTable';
import LoadingSpinner from '../components/LoadingSpinner';
import HeroForm from '../forms/HeroForm';
import toast from 'react-hot-toast';

const HeroModule = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    setLoading(true);
    try {
      const response = await getAllSlides();
      console.log('Hero slides response:', response);
      // Handle different response formats
      let slidesData = [];
      if (response && response.data && Array.isArray(response.data)) {
        slidesData = response.data;
      } else if (response && Array.isArray(response)) {
        slidesData = response;
      } else if (response && response.success && response.data && Array.isArray(response.data)) {
        slidesData = response.data;
      }
      setSlides(slidesData);
      console.log('Slides loaded:', slidesData.length);
    } catch (error) {
      console.error('Error fetching hero slides:', error);
      toast.error('Error fetching hero slides');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this slide? This action cannot be undone.')) {
      try {
        await deleteSlide(id);
        toast.success('Slide deleted successfully');
        fetchSlides();
      } catch (error) {
        toast.error('Error deleting slide');
      }
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await toggleSlideStatus(id);
      toast.success(`Slide ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchSlides();
    } catch (error) {
      toast.error('Error toggling status');
    }
  };

  const columns = ['Image', 'Title', 'Subtitle', 'Button', 'Order', 'Status', 'Actions'];

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `http://localhost:5000${url}`;
    return url;
  };

  const getStatusBadge = (isActive) => {
    return {
      bg: isActive ? '#D4EDDA' : '#F8D7DA',
      color: isActive ? '#155724' : '#721C24',
      text: isActive ? 'Active' : 'Inactive'
    };
  };

  const renderValue = (slide, column) => {
    if (column === 'Image') {
      return slide.imageUrl ? (
        <img src={getFullImageUrl(slide.imageUrl)} alt={slide.title} style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }} />
      ) : '-';
    }
    if (column === 'Title') return slide.title || '-';
    if (column === 'Subtitle') return slide.subtitle || '-';
    if (column === 'Button') return slide.buttonText || '-';
    if (column === 'Order') return slide.order || 0;
    if (column === 'Status') {
      const status = getStatusBadge(slide.isActive);
      return (
        <button onClick={() => handleToggle(slide._id, slide.isActive)} style={{ padding: '0.25rem 0.75rem', borderRadius: '20px', border: 'none', background: status.bg, color: status.color, cursor: 'pointer' }}>
          {status.text}
        </button>
      );
    }
    if (column === 'Actions') {
      return (
        <>
          <button onClick={() => { setEditingItem(slide); setShowModal(true); }} style={{ padding: '0.25rem 0.75rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem' }}>Edit</button>
          <button onClick={() => handleDelete(slide._id)} style={{ padding: '0.25rem 0.75rem', background: '#DC2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Delete</button>
        </>
      );
    }
    return '-';
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ color: '#0A192F', fontSize: '1.3rem', marginBottom: '0.25rem' }}>Hero Slides</h2>
          <p style={{ color: '#5A6E7F' }}>Manage carousel slides for the hero section ({slides.length} slides)</p>
        </div>
        <button onClick={() => { setEditingItem(null); setShowModal(true); }} style={{ padding: '0.5rem 1rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="fas fa-plus" /> Add New Slide
        </button>
      </div>

      {slides.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', background: '#F7F9FC', borderRadius: '12px' }}>
          <i className="fas fa-images" style={{ fontSize: '3rem', color: '#CBD5E1', marginBottom: '1rem' }} />
          <p style={{ color: '#5A6E7F' }}>No hero slides found. Click "Add New Slide" to create one.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead style={{ background: '#F7F9FC' }}>
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx} style={{ padding: '1rem', textAlign: 'left' }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slides.map((slide) => (
                <tr key={slide._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                  {columns.map((col, colIdx) => (
                    <td key={colIdx} style={{ padding: '1rem' }}>{renderValue(slide, col)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <HeroForm 
          editingItem={editingItem} 
          onClose={() => { setShowModal(false); setEditingItem(null); }} 
          onRefresh={fetchSlides} 
        />
      )}
    </div>
  );
};

export default HeroModule;